import React from "react";

const ThreePAbout = () => {
  return (
    <div className="bg-black text-white [&_p]:text-white min-h-screen p-4 mt-2">

      {/* Header */}
      <div className="mx-auto">

        <h1 className="text-lg lg:text-xl font-bold text-yellow-200 mb-2">
          🧠 Three-Point Estimation
        </h1>

        <h1 className="text-xs lg:text-sm text-gray-100 mb-6">
          A Practical Thinking Tool for Estimation, Risk and Decision Making
        </h1>

        <p className="text-xs lg:text-base mb-4">
          Three-Point Estimation is a powerful thinking method that helps people
          estimate tasks using <span className="font-semibold">ranges instead of single numbers</span>.
          It trains the brain to think in terms of uncertainty, probability and
          realistic planning.
        </p>

        <p className="text-xs lg:text-base mb-6">
          This concept connects mathematics, statistics, project planning and
          decision science. It is widely used in engineering and large scale
          projects to reduce estimation errors.
        </p>


        {/* Planning Bias */}
        <div className="bg-yellow-500 border-l-4 border-yellow-400 p-4 rounded mb-8">
          <p className="text-xs lg:text-base">
            ⚠️ The human brain has <span className="font-semibold">planning bias</span>.
            People naturally underestimate the time and effort required for tasks.
            Three-Point Estimation helps correct this bias.
          </p>
        </div>


        {/* Inputs */}
        <h2 className="text-lg lg:text-2xl font-semibold text-purple-800 mb-3">
          🔢 Fundamental Inputs
        </h2>

        <div className="text-xs lg:text-base space-y-1 mb-6">
          <p><span className="font-semibold">O</span> = Optimistic estimate</p>
          <p><span className="font-semibold">M</span> = Most likely estimate</p>
          <p><span className="font-semibold">P</span> = Pessimistic estimate</p>
        </div>


        {/* Example */}
        <div className="bg-gray-600 p-4 rounded-lg mb-8">

          <h3 className="font-semibold mb-2 text-sm lg:text-lg">
            💻 Example: Website Development Task
          </h3>

          <div className="text-xs lg:text-base">
            <p>Optimistic (O): 4 days</p>
            <p>Most Likely (M): 7 days</p>
            <p>Pessimistic (P): 16 days</p>
          </div>

        </div>


        {/* Triangular */}
        <div className="mb-8">

          <h3 className="text-sm lg:text-lg font-semibold text-blue-700 mb-2">
            📊 A. Triangular Distribution
          </h3>

          <p className="text-xs lg:text-base mb-2">
            Formula: <span className="font-mono">(O + M + P) / 3</span>
          </p>

          <p className="text-xs lg:text-base">
            (4 + 7 + 16) / 3 = 27 / 3 = <span className="font-semibold">9 days</span>
          </p>

        </div>


        {/* PERT */}
        <div className="mb-8">

          <h3 className="text-sm lg:text-lg font-semibold text-green-700 mb-2">
            📈 B. PERT Estimate (Weighted)
          </h3>

          <p className="text-xs lg:text-base mb-2">
            Formula: <span className="font-mono">(O + 4M + P) / 6</span>
          </p>

          <p className="text-xs lg:text-base">
            (4 + 4×7 + 16) / 6 = 48 / 6 = <span className="font-semibold">8 days</span>
          </p>

          <p className="text-xs lg:text-base mt-2 text-gray-700">
            PERT gives more weight to the <span className="font-semibold">Most Likely scenario</span>.
          </p>

        </div>


        {/* Why Single Estimates Fail */}
        <h2 className="text-lg lg:text-2xl font-semibold text-purple-800 mb-3">
          ⚠️ Why Single Estimates Fail
        </h2>

        <div className="text-xs lg:text-base mb-8">
          <p>Most people say: “This project will take 10 days.”</p>
          <p>But reality usually looks like this:</p>

          <div className="mt-2 space-y-1">
            <p>Best Case → 5 days</p>
            <p>Most Likely → 10 days</p>
            <p>Worst Case → 20 days</p>
          </div>

        </div>


        {/* Companies */}
        <h2 className="text-lg lg:text-2xl font-semibold text-purple-800 mb-3">
          🏢 How Smart Companies Plan
        </h2>

        <p className="text-xs lg:text-base mb-4">
          Large engineering programs such as the United States Navy’s Polaris
          missile project used PERT estimation to manage uncertainty in complex projects.
        </p>

        <p className="text-xs lg:text-base mb-8">
          Today similar thinking is used in many technology companies including
          Amazon, Google, Microsoft and SpaceX.  
          Instead of single estimates, they plan with ranges.
        </p>


        {/* Thinking Stack */}
        <h2 className="text-lg lg:text-2xl font-semibold text-purple-800 mb-3">
          🧠 Think5Whys Thinking Skills Stack
        </h2>

        <div className="text-xs lg:text-base space-y-1 mb-8">

          <p>🔎 Root Cause → 5 Whys</p>
          <p>📊 Estimation → Three-Point Estimate</p>
          <p>⚠️ Risk → Scenario Analysis</p>
          <p>📈 Execution → PERT Planning</p>

        </div>


        {/* 3D Thinking Model */}
        <h2 className="text-lg lg:text-2xl font-semibold text-purple-800 mb-3">
          🚀 Think5Whys 3D Thinking Model
        </h2>

        <div className="text-xs lg:text-base space-y-2 mb-8">

          <p>❓ WHY Thinking → Root Cause</p>
          <p>📏 RANGE Thinking → Estimation</p>
          <p>⚠️ RISK Thinking → Decision</p>

        </div>


        {/* Platform Vision */}
        <h2 className="text-lg lg:text-2xl font-semibold text-purple-800 mb-3">
          🌍 Think5Whys Platform Vision
        </h2>

        <p className="text-xs lg:text-base mb-4">
          Think5Whys combines Root Cause Thinking, Three-Point Estimation,
          AI analysis and task management to build a new category of software:
        </p>

        <div className="bg-purple-500 border border-purple-200 p-4 rounded-lg mb-8">
          <p className="text-xs lg:text-base font-semibold text-purple-800">
            🧠 Structured Thinking Infrastructure
          </p>
        </div>


        {/* Market */}
        <h2 className="text-lg lg:text-2xl font-semibold text-purple-800 mb-3">
          📊 Where Three-Point Estimation Is Used
        </h2>

        <div className="text-xs lg:text-base space-y-1 mb-8">
          <p>💻 Software development</p>
          <p>🏭 Manufacturing planning</p>
          <p>🏗 Construction projects</p>
          <p>📦 Logistics and delivery</p>
          <p>📈 Consulting and project delivery</p>
          <p>🚀 Startup product roadmaps</p>
        </div>


        {/* Education */}
        <h2 className="text-lg lg:text-2xl font-semibold text-purple-800 mb-3">
          🎓 Education Opportunity
        </h2>

        <p className="text-xs lg:text-base mb-6">
          Schools rarely teach estimation, risk thinking and structured decision
          making. Practical Thinking Mathematics introduces these skills early
          so students learn how real-world planning works.
        </p>


        {/* Closing */}
        <div className="bg-gray-700 p-4 rounded-lg">

          <p className="text-xs lg:text-base font-semibold">
            Think5Whys aims to become the Operating System for Decision Making.
          </p>

          <p className="text-xs lg:text-base mt-2">
            Problem → 5 Whys  
            Estimate → 3-Point Thinking  
            Plan → PERT  
            Execute → Transformation
          </p>

        </div>

      </div>
    </div>
  );
};

export default ThreePAbout;